import React, { useState, useMemo, useRef, useEffect } from "react";
import { Grid } from "@mui/material";

import { UseUserId } from "../../../hooks/auth";
import GoldCrown from "../../../img/arena/GoldCrown.svg";
import SilverCrown from "../../../img/arena/SilverCrown.svg";
import BronzeCrown from "../../../img/arena/BronzeCrown.svg";
import SoftProfile from "../../ProfilePictures/SoftProfile";
import useMediaQuery from "@mui/material/useMediaQuery";
import SoftTypography from "../../SoftTypography";
import SoftBox from "../../SoftBox";

const PLACES = [1, 0, 2];

const ORDER = ["1ST", "2ND", "3RD"];
// Podium ordinal sizes — was 3rem/2.5rem/2rem (48/40/32). Dropped a tier
// so 1st/2nd/3rd don't shout. The crown + bar height already communicates
// "winner" — the ordinal label doesn't need to do the same job again.
const FONTSIZE = ["2rem", "1.75rem", "1.5rem"];
const FONTSIZE_MOBILE = ["2rem", "1.5rem", "1.5rem"];
const COLORS = ["primary", "tertiary", "secondary"];
const PODIUMS = [1, 0.85, 0.75];

const CROWNS = [GoldCrown, SilverCrown, BronzeCrown];

// fluid: the standalone dashboard podium block can be a quarter of the page
// or the full row, so every size derives from the MEASURED container width
// instead of screen breakpoints. Without it, behavior is unchanged.
const Leaders = ({ top3, fluid = false }) => {
  const [podiums] = useState(PODIUMS);
  const userId = UseUserId();
  const userIndex = top3.findIndex((data) => data.userId === userId);
  const textBp = useMediaQuery((theme) => theme.breakpoints.down("lg"));
  const smallBp = useMediaQuery((theme) => theme.breakpoints.down("xxs"));
  const isSM = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const isMidsizeScreen = useMediaQuery((theme) => theme.breakpoints.down("md"));
  const screenWidth = window.innerWidth;

  const boxRef = useRef(null);
  const [cw, setCw] = useState(0);
  useEffect(() => {
    if (!fluid || !boxRef.current) return undefined;
    const el = boxRef.current;
    const ro = new ResizeObserver((entries) => {
      if (entries[0]) setCw(entries[0].contentRect.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [fluid]);

  // Container-derived sizes (fluid mode only). Pedestals are PERCENTAGES of
  // the container height so the podium always rises from the card floor and
  // owns the card — no dead air above or below at any block size.
  const F = useMemo(() => {
    if (!fluid) return null;
    const w = cw || 296;
    let avatar = "xl";
    if (w < 300) avatar = "md";
    else if (w < 560) avatar = "lg";
    return {
      pedestal: 56, // 1st place pedestal = 56% of the container height
      avatar,
      crown: w < 300 ? "1.1rem" : "1.6rem",
      ordinal: w < 300 ? "0.95rem" : "1.25rem",
      ordinalFull: w >= 260,
      nameFs: w < 420 ? "11px" : "13px",
      pillFs: w < 420 ? "10.5px" : "12.5px",
      pillPx: w < 420 ? 1 : 2.5,
    };
  }, [fluid, cw]);

  const podiumConfig = useMemo(() => {
    // Supports only up to 320px mobile screen width
    if (smallBp) {
      return { container: 200, elem: 205, ratio: 1.1, platform: 0.2 }
    } else if (isSM) {
      return { container: 240, elem: 240, ratio: 1.1, platform: 0.5 }
    }
    else if (isMidsizeScreen) {
      return { container: 300, elem: 300, ratio: 1.1, platform: 0.5 }
    }
    else {
      return { container: 420, elem: 450, ratio: 1.1, platform: 0.5 }
    }
  }, [isSM, smallBp, isMidsizeScreen]);

  return (
    <SoftBox
      ref={boxRef}
      className="container w-full h-full flex flex-col justify-center items-center"
      sx={{
        paddingBottom: fluid ? 0 : { mini: 4, lg: 0 },
      }}
    >
      <Grid container className={`w-full h-full min-h-[${podiumConfig.container}px] flex items-end`}>
        {PLACES.map((rank, idx) => {
          return (
            <Grid
              item
              key={rank}
              mini={4}
              className="w-full h-full flex flex-col items-end"
            >
              <SoftBox
                className="w-full flex flex-col items-center px-[1px] transition-height duration-500"
                sx={{
                  ...(F
                    ? {
                        height: "100%",
                        minHeight: 0,
                        justifyContent: "flex-end",
                      }
                    : {
                        height: `${podiums[rank]}`,
                        minHeight: `${podiums[rank] * 450}px`,
                        ...(isMidsizeScreen && ({
                          height: `${podiums[rank] * podiumConfig.ratio}`,
                          minHeight: `${podiums[rank] * podiumConfig.ratio * podiumConfig.elem}px`,
                        })),
                      }),
                }}
              >
                <SoftBox className="flex flex-col items-center" sx={F ? { width: "100%", minWidth: 0 } : undefined}>
                  <SoftBox
                    className={F ? "w-full flex justify-center" : "w-full flex justify-center pb-[1rem]"}
                    sx={F ? { paddingBottom: "0.4rem" } : undefined}
                  >
                    {F ? (
                      <img src={CROWNS[rank]} alt={`Top ${rank + 1}`} style={{ width: "auto", height: F.crown }} />
                    ) : (
                      <>
                        {rank === 0 ? (
                          <img src={GoldCrown} alt="Top 1" className={`w-auto h-[${isSM ? 1.5 : 3}rem]`} />
                        ) : null}
                        {rank === 1 ? (
                          <img src={SilverCrown} alt="Top 2" className={`w-auto h-[${isSM ? 1.5 : 3}rem]`} />
                        ) : null}
                        {rank === 2 ? (
                          <img src={BronzeCrown} alt="Top 3" className={`w-auto h-[${isSM ? 1.5 : 3}rem]`} />
                        ) : null}
                      </>
                    )}
                  </SoftBox>
                  <SoftProfile
                    src={top3[rank]?.image}
                    name={top3[rank]?.firstName}
                    // eslint-disable-next-line no-nested-ternary
                    size={F ? F.avatar : (smallBp ? "lg" : textBp ? "xl" : "xxl")}
                    glow={userIndex === rank}
                    fullCover
                  />
                  <SoftBox
                    sx={{
                      display: "flex",
                      width: "100%",
                      justifyContent: "center",
                      gap: "0.25rem",
                      flexWrap: "wrap",
                      marginY: F ? "0.5rem" : "1rem",
                      minWidth: 0,
                    }}
                  >
                    <SoftTypography
                      color="#191D23"
                      sx={{
                        // Podium player name — was h5/h6 (~18px / heavy).
                        // Now 14/600 so the focus stays on the bars and
                        // the names act as captions.
                        fontFamily: "DM Sans",
                        position: "relative",
                        textAlign: "center",
                        fontSize: "14px",
                        fontWeight: 600,
                        lineHeight: 1.2,
                        ...(isMidsizeScreen && ({ fontSize: "11px !important", lineHeight: "15px", color: "#98A2B3" })),
                        ...(F && ({ fontSize: `${F.nameFs} !important`, lineHeight: 1.25, maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" })),
                      }}
                    >
                      {top3[rank]?.firstName || "Anonymous"}
                    </SoftTypography>
                    {!(F && cw && cw < 240) && (
                      <SoftTypography
                        color="#191D23"
                        sx={{
                          fontFamily: "DM Sans",
                          position: "relative",
                          textAlign: "center",
                          fontSize: "14px",
                          fontWeight: 600,
                          lineHeight: 1.2,
                          ...(isMidsizeScreen && ({ fontSize: "11px !important", lineHeight: "15px", color: "#98A2B3" })),
                          ...(F && ({ fontSize: `${F.nameFs} !important`, lineHeight: 1.25, maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" })),
                        }}
                      >
                        {top3[rank]?.lastName || "Player"}
                      </SoftTypography>
                    )}
                  </SoftBox>
                </SoftBox>
                <SoftBox
                  className="flex flex-col flex-1 w-full text-white"
                  sx={F ? { flex: "0 0 auto", width: "100%", height: `${Math.round(podiums[rank] * F.pedestal)}%`, minHeight: "110px" } : undefined}
                >
                  <SoftBox
                    sx={{
                      width: `100% + ${podiumConfig.platform}rem`,
                      height: 0,
                    }}
                  />

                  <SoftBox
                    className="flex-1 w-full flex justify-center relative"
                    sx={{ bgcolor: `${COLORS[rank]}.main` }}
                  >
                    {(!isMidsizeScreen || F) &&
                      <SoftBox
                        className="absolute rounded-full py-1 text-center"
                        sx={{
                          bgcolor: "rgba(0,0,0,0.25)",
                          bottom: F ? "0.6rem" : "1rem",
                          paddingX: F ? F.pillPx : { mini: 2, sm: 4 },
                        }}
                      >
                        <SoftTypography
                          sx={{
                            // Podium points value pill at the bottom of
                            // each bar — was h5 / bold. Now 14/600 with
                            // tabular numerals so it reads as a clean
                            // data chip, not a headline.
                            fontFamily: "DM Sans",
                            color: "secondary.contrastText",
                            fontSize: "14px",
                            fontWeight: 600,
                            fontFeatureSettings: '"tnum" 1',
                            letterSpacing: "-0.005em",
                            ...(screenWidth <= 1280 && ({ fontSize: "13px" })),
                            ...(F && ({ fontSize: `${F.pillFs} !important` })),
                          }}
                        >
                          {top3[rank]?.scores.total
                            ? Math.round(top3[rank].scores.total)
                            : 0}
                        </SoftTypography>
                      </SoftBox>
                    }
                    <SoftBox
                      sx={{
                        position: "absolute",
                        top: F ? "0.4rem" : { md: "0.5rem" },
                      }}
                    >
                      <SoftTypography
                        className={isMidsizeScreen && !F ? "none" : "font-bold"}
                        sx={{
                          fontSize: (isSM)
                            ? FONTSIZE_MOBILE[rank]
                            : FONTSIZE[rank],
                          ...(smallBp && rank === 2  && ({ fontSize: "1rem !important" })),
                          color: "secondary.contrastText",
                          ...(screenWidth <= 1280 && ({ fontSize: "1.25rem" })),
                          ...(F && ({ fontSize: `${F.ordinal} !important` })),
                        }}
                      >
                        {(() => {
                          if (F) return F.ordinalFull ? ORDER[rank] : ORDER[rank].substring(0, 1);
                          return isMidsizeScreen ? ORDER[rank].substring(0, 1) : ORDER[rank];
                        })()}
                      </SoftTypography>
                    </SoftBox>
                  </SoftBox>
                </SoftBox>
              </SoftBox>
            </Grid>
          );
        })}
      </Grid>
    </SoftBox>
  );
};

export default Leaders;
